import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { newRef, uploadPublic, errorText, MIN_PHOTOS } from "@/lib/publishQueue";
import { shipRange, hasShipRates } from "@/lib/sellerNet";
import { invalidateCatalogCache } from "@/lib/catalogCache";

export const LOT_TYPES = [
  { key: "bulk", label: "Bulk", hint: "Cartas sueltas por cantidad" },
  { key: "deck", label: "Deck", hint: "Mazo armado y jugable" },
  { key: "collection", label: "Colección", hint: "Set o carpeta completa" },
];
export const LOT_LANGS = [{ key: "es", label: "Español" }, { key: "en", label: "Inglés" }, { key: "otro", label: "Mixto" }];
export const LOT_CONDITIONS = [{ key: "NM", label: "NM" }, { key: "LP", label: "LP" }, { key: "MIXED", label: "Mixta" }];

const initial = () => ({ subtype: "bulk", title: "", approx_cards: "", price: "", language: "es", condition: "NM", contents: "" });

// P6 · lote en un solo envío: validación, "Te faltan N fotos" y publicación al instante (idempotente).
export function useLotForm({ game, shippingConfig, onPublished }) {
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(newRef());
  const lock = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const missingPhotos = Math.max(0, MIN_PHOTOS - photos.length);
  const missing = [
    form.title.trim().length < 3 && "título",
    !(parseInt(form.approx_cards, 10) >= 1) && "cartas aprox.",
    !(parseFloat(form.price) >= 0.25) && "precio",
    !hasShipRates(shippingConfig) && "envío",
  ].filter(Boolean);
  const canSubmit = !busy && !missing.length && !missingPhotos;
  const buttonLabel = busy ? "Publicando…"
    : missingPhotos ? (missingPhotos === 1 ? "Te falta 1 foto" : `Te faltan ${missingPhotos} fotos`)
    : missing.length ? (missing.length > 1 ? `Te faltan ${missing.length} datos` : "Te falta 1 dato") : "Publicar lote";

  const submit = async () => {
    if (!canSubmit || lock.current) return null;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const paths = await Promise.all(photos.map((f) => uploadPublic(f)));
      const { data } = await api.post("/lots", {
        ...form, local_only: form.local_only ?? !!shippingConfig?.local_only, approx_cards: parseInt(form.approx_cards, 10), price: parseFloat(form.price),
        photo_paths: paths, ship_cost: shipRange(shippingConfig)[0], game, client_ref: ref.current,
      });
      try { invalidateCatalogCache(); } catch { /* noop */ }
      setForm(initial());
      setPhotos([]);
      ref.current = newRef();
      onPublished?.(data);
      return data;
    } catch (err) {
      setError(errorText(err));
      return null;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return { form, set, photos, setPhotos, missing, missingPhotos, canSubmit, buttonLabel, submit, busy, error };
}
